import { useEffect, useRef, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * PolishTitleButton — one-tap "let Willoah tighten this title" that returns
 * three options in a small popover under the button. Tap any option to
 * accept it (parent's `onPick` gets the chosen string).
 *
 * Sits inline next to a Title input. Reads the current draft via `draft`
 * (title / category / context) and POSTs to /api/ideas/polish-title.
 *
 * The popover closes on outside-click, on Escape, and after a pick.
 */
export default function PolishTitleButton({ draft, onPick, disabled, testid = "polish-title-btn" }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const wrapRef = useRef(null);

  // Outside-click + Escape to close. Only bound while open so we don't
  // sink events on every idle render.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const run = async () => {
    if (busy || disabled) return;
    if (!draft?.title?.trim()) {
      toast("Type a title first — we need something to polish.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ideas/polish-title", {
        title: draft.title || "",
        category: draft.category || "",
        context: draft.context || "",
      });
      const list = Array.isArray(data?.suggestions) ? data.suggestions.filter((s) => (s || "").trim()) : [];
      if (list.length === 0) {
        toast("The model came back empty — try again.");
        return;
      }
      setSuggestions(list);
      setOpen(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Couldn't polish that title right now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const pick = (title) => {
    onPick?.(title);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onClick={run}
        disabled={busy || disabled}
        data-testid={testid}
        className="inline-flex items-center gap-1.5 text-xs text-[#4E5A3D] hover:text-[#3E4830] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        title="Ask Willoah to polish this title"
      >
        <Sparkles className={`w-3.5 h-3.5 ${busy ? "animate-pulse" : ""}`} strokeWidth={1.75} />
        {busy ? "Polishing…" : "Polish my title"}
      </button>

      {open && suggestions.length > 0 && (
        <div
          role="menu"
          data-testid={`${testid}-popover`}
          className="absolute z-30 mt-2 right-0 min-w-[280px] max-w-[360px] rounded-2xl bg-[#FBF8F1] border border-[#B79256]/40 shadow-[0_16px_40px_rgb(42,40,35,0.15)] p-2"
        >
          <div className="flex items-center justify-between px-2 pb-1.5">
            <div className="accent-label text-[10px] text-[#B79256]">Pick one</div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="p-1 rounded-full text-[#2A2823]/50 hover:text-[#2A2823] hover:bg-[#B79256]/12"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.75} />
            </button>
          </div>
          <ul className="space-y-1">
            {suggestions.map((s, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => pick(s)}
                  data-testid={`${testid}-option-${i}`}
                  className="w-full text-left px-3 py-2.5 rounded-xl bg-white/70 hover:bg-[#4E5A3D]/8 border border-[#B79256]/25 text-sm text-[#2A2823] leading-snug"
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>
          <p className="px-2 pt-1.5 pb-1 text-[10px] text-[#2A2823]/50 italic">
            Tapping replaces your current title. You can still edit after.
          </p>
        </div>
      )}
    </div>
  );
}
